<template>
  <div class="main-container">
    <el-card class="filter-container" shadow="never">
      <div>
        <i class="el-icon-search"></i>
        <span>筛选搜索</span>
        <div style="float: right; display: flex; align-items: center;">
          <el-radio-group v-model="viewMode" size="small" style="margin-right: 15px;">
            <el-radio-button label="list">列表视图</el-radio-button>
            <el-radio-button label="card">房间视图</el-radio-button>
          </el-radio-group>
          <el-button
            @click="handleAddRoom"
            type="primary"
            size="small">
            添加房间
          </el-button>
        </div>
      </div>
      <div class="form-container">
        <el-form :inline="true" :model="searchForm" size="small" label-width="140px">
          <el-form-item label="房间名称">
            <el-input v-model="searchForm.roomName" placeholder="房间名称" clearable></el-input>
          </el-form-item>
          <el-form-item label="楼层">
            <el-input v-model="searchForm.floor" placeholder="楼层" clearable></el-input>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="searchForm.status" placeholder="请选择" clearable>
              <el-option label="可用" value="可用"></el-option>
              <el-option label="维修中" value="维修中"></el-option>
              <el-option label="停用" value="停用"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="handleSearch">查询</el-button>
            <el-button @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
    
    <!-- 列表视图 -->
    <el-card class="table-container" v-if="viewMode === 'list'">
      <el-table
        v-loading="listLoading"
        :data="roomList"
        border
        style="width: 100%"
        @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="60" align="center"></el-table-column>
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column prop="roomName" label="房间名称" align="center"></el-table-column>
        <el-table-column prop="floor" label="楼层" width="100" align="center"></el-table-column>
        <el-table-column prop="size" label="面积(㎡)" width="100" align="center"></el-table-column>
        <el-table-column prop="capacity" label="容纳人数" width="100" align="center"></el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.status)">{{ scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="房间简介" :show-overflow-tooltip="true"></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180" align="center">
          <template slot-scope="scope">{{ scope.row.createTime | formatDate }}</template>
        </el-table-column>
        <el-table-column label="操作" width="250" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleViewRoom(scope.row)">查看</el-button>
            <el-button type="text" size="small" @click="handleEditRoom(scope.row)">编辑</el-button>
            <el-button 
              type="text" 
              size="small" 
              @click="handleUpdateStatus(scope.row)"
              :style="{color: getStatusActionColor(scope.row.status)}">
              {{ getStatusActionText(scope.row.status) }}
            </el-button>
            <el-button type="text" size="small" @click="handleViewCalendar(scope.row)" style="color: #409eff">日历</el-button>
            <el-button type="text" size="small" @click="handleDeleteRoom(scope.row)" style="color: #F56C6C">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page.sync="currentPage"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>
    
    <!-- 房间视图 -->
    <el-card class="card-container" v-else>
      <div class="room-view-header">
        <div class="room-stats">
          <el-tag>全部: {{ roomList.length }}</el-tag>
          <el-tag type="success">可用: {{ availableRooms.length }}</el-tag>
          <el-tag type="primary">使用中: {{ inUseRooms.length }}</el-tag>
          <el-tag type="warning">其他: {{ otherRooms.length }}</el-tag>
        </div>
        <el-button 
          type="text" 
          icon="el-icon-refresh" 
          @click="refreshRooms"
          :loading="statusLoading">
          刷新
        </el-button>
      </div>
      <div v-loading="listLoading || statusLoading" class="room-view-container">
        <floor-group
          v-for="(rooms, floor) in roomsByFloor"
          :key="floor"
          :title="floor ? `${floor}楼` : '未分类'"
          :rooms="rooms"
          @room-click="handleRoomCardClick"
        ></floor-group>
      </div>
      <div v-if="Object.keys(roomsByFloor).length === 0" class="no-data">
        <i class="el-icon-warning"></i>
        <p>暂无房间数据</p>
      </div>
      <div class="pagination-container" v-if="viewMode === 'card' && total > pageSize">
        <el-pagination
          background
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page.sync="currentPage"
          :page-sizes="[20, 50, 100, 200]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>
    
    <!-- 房间操作对话框 -->
    <el-dialog
      :title="`房间操作 - ${selectedRoom.roomName || ''}`"
      :visible.sync="roomDialogVisible"
      width="30%">
      <div class="room-dialog-content">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="房间号">{{ selectedRoom.roomName }}</el-descriptions-item>
          <el-descriptions-item label="楼层">{{ selectedRoom.floor }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusType(selectedRoom.status)">{{ selectedRoom.status }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="使用状态">
            <el-tag :type="selectedRoom.occupationStatus === '服务中' ? 'primary' : selectedRoom.occupationStatus === '洗浴' ? 'warning' : 'success'">
              {{ selectedRoom.occupationStatus || '空闲' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="客户" v-if="selectedRoom.customerInfo">
            {{ selectedRoom.customerInfo.name }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="roomDialogVisible = false">关 闭</el-button>
        <el-button type="primary" @click="handleEditRoom(selectedRoom)">编 辑</el-button>
        <el-button type="primary" @click="handleViewCalendar(selectedRoom)">查看日历</el-button>
        <el-button type="success" @click="handleAddTemporaryAppointment(selectedRoom)">添加临时预约</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { formatDate } from '@/utils/date'
import FloorGroup from './components/FloorGroup.vue'

export default {
  components: {
    FloorGroup
  },
  filters: {
    formatDate(time) {
      if (time) {
        return formatDate(new Date(time), 'YYYY-MM-DD HH:mm:ss')
      }
      return ''
    }
  },
  data() {
    return {
      viewMode: localStorage.getItem('roomViewMode') || 'list',
      searchForm: {
        roomName: '',
        floor: '',
        status: ''
      },
      roomList: [],
      roomStatusMap: {}, // 房间使用状态映射
      listLoading: false,
      statusLoading: false,
      multipleSelection: [],
      currentPage: 1,
      pageSize: this.getPageSizeForViewMode('list'),
      total: 0,
      roomDialogVisible: false,
      selectedRoom: {},
      statusRefreshInterval: null
    }
  },
  computed: {
    // 按楼层分组的房间
    roomsByFloor() {
      // 创建增强版的房间列表，包括使用状态
      const enhancedRooms = this.roomList.map(room => {
        const status = this.roomStatusMap[room.id] || {};
        return {
          ...room,
          occupationStatus: status.status,
          customerInfo: status.customerInfo
        };
      });
      
      // 按楼层分组
      return enhancedRooms.reduce((groups, room) => {
        const floor = room.floor || '未分类';
        if (!groups[floor]) groups[floor] = [];
        groups[floor].push(room);
        return groups;
      }, {});
    },
    // 可用房间
    availableRooms() {
      return this.roomList.filter(room => {
        // 房间基本状态必须是"可用"
        if (room.status !== '可用') return false;
        
        // 检查实时状态 - 如果有实时状态且为"服务中"则不是可用的
        if (this.roomStatusMap[room.id] && 
            this.roomStatusMap[room.id].status === '服务中') {
          return false;
        }
        
        return true;
      });
    },
    // 使用中房间
    inUseRooms() {
      return this.roomList.filter(room => 
        this.roomStatusMap[room.id] && 
        this.roomStatusMap[room.id].status === '服务中'
      );
    },
    // 其他状态房间
    otherRooms() {
      return this.roomList.filter(room => {
        // 基本状态不是"可用"的房间
        if (room.status !== '可用') return true;
        
        // 有实时状态但不是"服务中"的房间（比如"维修中"）
        if (this.roomStatusMap[room.id] && 
            this.roomStatusMap[room.id].status && 
            this.roomStatusMap[room.id].status !== '服务中' &&
            this.roomStatusMap[room.id].status !== '可用') {
          return true;
        }
        
        return false;
      });
    }
  },
  created() {
    this.getRoomList()
    this.getRoomStatus();
    // 设置定时刷新（每30秒）
    this.statusRefreshInterval = setInterval(() => {
      console.log("自动刷新房间状态...");
      this.getRoomStatus();
    }, 30000);
  },
  watch: {
    viewMode(newVal) {
      localStorage.setItem('roomViewMode', newVal);
      // 根据视图模式调整每页记录数
      this.pageSize = this.getPageSizeForViewMode(newVal);
      this.getRoomList();
      // if (newVal === 'card') {
        this.getRoomStatus();
      // }
    }
  },
  beforeDestroy() {
    // 组件销毁前清除定时器
    if (this.statusRefreshInterval) {
      clearInterval(this.statusRefreshInterval);
    }
  },
  methods: {
    getPageSizeForViewMode(mode) {
      return mode === 'list' ? 10 : 100;
    },
    getStatusType(status) {
      if (status === '可用') return 'success'
      if (status === '维修中') return 'warning'
      if (status === '停用') return 'danger'
      return 'info'
    },
    getStatusActionText(status) {
      if (status === '可用') return '设为维修'
      if (status === '维修中') return '设为可用'
      if (status === '停用') return '设为可用'
      return '更改状态'
    },
    getStatusActionColor(status) {
      if (status === '可用') return '#E6A23C' // 橙色
      if (status === '维修中' || status === '停用') return '#67C23A' // 绿色
      return '#409EFF' // 蓝色
    },
    getRoomList() {
      this.listLoading = true
      const params = {
        page: this.currentPage,
        limit: this.pageSize,
        sort: "id",
        roomName: this.searchForm.roomName ? "%" + this.searchForm.roomName + "%" : undefined,
        floor: this.searchForm.floor ? this.searchForm.floor : undefined,
        status: this.searchForm.status ? this.searchForm.status : undefined
      };
      this.$http({
        url: '/room/page',
        method: 'get',
        params
      }).then(({ data }) => {
        console.log("rooms",data);
        
        if (data && data.code === 0 && data.page && data.page.list) {
          this.roomList = data.page.list
          this.total = data.page.totalCount
        } else {
          this.roomList = []
          this.total = 0
        }
        this.listLoading = false
      }).catch((error) => {
        console.error("获取房间列表失败:", error);
        this.roomList = []
        this.total = 0
        this.listLoading = false
      })
    },
    // 获取房间使用状态
    getRoomStatus() {
      if (!this.roomList.length) return;
      
      this.statusLoading = true;
      
      // 调用后端API获取房间实时状态
      this.$http({
        url: '/room/status/realtime',
        method: 'get'
      }).then(({ data }) => {
        if (data && data.code === 0) {
          // 处理返回的房间状态数据
          this.roomStatusMap = data.data || {};
          console.log("获取到房间实时状态:", this.roomStatusMap);
          
          // 触发状态变化事件，可用于通知其他组件
          this.$emit('room-status-updated', this.roomStatusMap);
        } else {
          console.warn("获取房间状态返回异常:", data);
          this.roomStatusMap = {};
        }
        this.statusLoading = false;
      }).catch(error => {
        console.error("获取房间状态失败:", error);
        this.statusLoading = false;
        
        // 如果API请求失败，使用模拟数据（仅在开发环境中）
        if (process.env.NODE_ENV !== 'production') {
          console.log("使用模拟数据作为备选");
          // 模拟数据 - 仅作为备选方案
          const mockStatus = {};
          this.roomList.forEach(room => {
            // 随机生成房间状态用于演示
            const randomStatus = Math.random();
            if (randomStatus < 0.3 && room.status === '可用') {
              mockStatus[room.id] = {
                status: '服务中',
                customerInfo: {
                  name: `客户${Math.floor(Math.random() * 100)}`,
                  project: `项目${Math.floor(Math.random() * 10)}`,
                  price: Math.floor(Math.random() * 500) + 100
                }
              };
            } else if (randomStatus < 0.4 && room.status === '可用') {
              mockStatus[room.id] = {
                status: '洗浴',
                customerInfo: {
                  name: `客户${Math.floor(Math.random() * 100)}`,
                  project: '洗浴',
                  price: 99
                }
              };
            }
          });
          
          this.roomStatusMap = mockStatus;
          this.statusLoading = false;
        }
      });
    },
    // 处理房间卡片点击
    handleRoomCardClick(room) {
      this.selectedRoom = room;
      this.roomDialogVisible = true;
    },
    handleSearch() {
      this.currentPage = 1
      this.getRoomList()
      if (this.viewMode === 'card') {
        this.getRoomStatus();
      }
    },
    resetSearch() {
      this.searchForm = {
        roomName: '',
        floor: '',
        status: ''
      }
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.getRoomList()
      if (this.viewMode === 'card') {
        this.getRoomStatus();
      }
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getRoomList()
      if (this.viewMode === 'card') {
        this.getRoomStatus();
      }
    },
    handleSelectionChange(val) {
      this.multipleSelection = val
    },
    handleAddRoom() {
      this.$router.push('/room/add-or-update')
    },
    handleViewRoom(row) {
      this.$router.push({
        path: '/room/add-or-update',
        query: {
          id: row.id,
          readonly: true
        }
      })
    },
    handleEditRoom(row) {
      this.$router.push({
        path: '/room/add-or-update',
        query: {
          id: row.id
        }
      })
    },
    handleViewCalendar(row) {
      this.$router.push({
        path: '/roomcalendar',
        query: {
          roomId: row.id,
          roomName: row.roomName
        }
      })
    },
    handleAddTemporaryAppointment(row) {
      this.$router.push({
        path: '/roomcalendar',
        query: {
          roomId: row.id,
          roomName: row.roomName,
          openAddDialog: 'true' // 添加参数，表示需要打开添加预约对话框
        }
      })
    },
    handleUpdateStatus(row) {
      let newStatus = '可用'
      if (row.status === '可用') {
        newStatus = '维修中'
      }
      
      this.$confirm(`确认将房间状态修改为"${newStatus}"?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$http({
          url: '/room/status',
          method: 'post',
          params: {
            id: row.id,
            status: newStatus
          }
        }).then(({ data }) => {
          if (data && data.code === 0) {
            this.$message({
              message: '操作成功',
              type: 'success',
              duration: 1500,
              onClose: () => {
                this.getRoomList()
                if (this.viewMode === 'card') {
                  this.getRoomStatus();
                }
              }
            })
          } else {
            this.$message.error(data.msg || '操作失败')
          }
        })
      }).catch(() => {})
    },
    handleDeleteRoom(row) {
      this.$confirm('确认删除该房间?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$http({
          url: '/room/delete',
          method: 'post',
          data: [row.id]
        }).then(({ data }) => {
          if (data && data.code === 0) {
            this.$message({
              message: '操作成功',
              type: 'success',
              duration: 1500,
              onClose: () => {
                this.getRoomList()
                if (this.viewMode === 'card') {
                  this.getRoomStatus();
                }
              }
            })
          } else {
            this.$message.error(data.msg || '操作失败')
          }
        })
      }).catch(() => {})
    },
    // 手动刷新房间状态
    refreshRooms() {
      this.getRoomStatus();
      // 只有在列表视图下才需要重新加载列表
      if (this.viewMode === 'list') {
        this.getRoomList();
      }
    }
  }
}
</script>

<style scoped>
.main-container {
  padding: 20px;
}
.filter-container {
  margin-bottom: 20px;
}
.form-container {
  margin-top: 20px;
}
.table-container, .card-container {
  margin-bottom: 20px;
}
.pagination-container {
  margin-top: 20px;
  text-align: right;
}
.room-view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.room-stats {
  display: flex;
  gap: 10px;
}
.room-view-container {
  min-height: 400px;
}
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #909399;
}
.no-data i {
  font-size: 48px;
  margin-bottom: 20px;
}
</style> 